import axios from 'axios';
import { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import { useAuthStore } from '../store/authStore';
import { useIsAdminViewer } from './menu-visibility';
import { registerSessionCache } from './session-caches';

/**
 * 「직원 관리」(`/erp/hr/employees*`)에 들어갈 수 있는가 — 역할 **또는** 인사 메뉴를 명시적으로 받은 사람 (2026-09-14).
 *
 * ── 왜 생겼나 ────────────────────────────────────────────────────────────
 * 서버는 판 5(2026-08-28)부터 이 화면들의 API 를 `isHrAdminOrGranted(auth, 'hr')` 로 연다 —
 * **인사 담당자는 STAFF 이고, 인사팀 부서에 인사 메뉴를 켜서 쓴다.** 그런데 라우터는
 * `RoleGuard(ADMIN·SYSTEM_ADMIN)` 로 막아, 서버가 여는 사람을 화면이 대시보드로 돌려보냈다.
 *
 * ★★ 프론트에는 「명시적으로 받았는가」 신호가 없다 — `useIsAdminViewer` 는 역할만 보고,
 *    메뉴 체계의 기본값은 「규칙이 없으면 보인다」라 **보이는가**로는 가를 수 없다(hr-menu-guard.ts 머리말).
 *    그래서 **서버에 한 번 묻는다**: 목록 API 를 아무도 안 걸리는 검색어로 부른다(`limit` 칸이 없어 가장 가벼운 모양).
 *
 * ★ 판정: 200 = 들어간다 · **403 만** 막는다. 429 · 5xx · 끊김은 「미확정」 — 종전 RoleGuard 처럼 막되 **캐시하지 않는다**(다음에 다시 묻는다).
 * ★ 확정된 답(200 · 403)은 모듈 캐시에 둔다 — 셸 트리와 가드가 같은 답을 두 번 받지 않게. 로그아웃하면 비운다(`session-caches`).
 * ★ 회사 관리자 · 시스템관리자는 묻지 않는다(종전과 같다).
 * ⚠ 프론트 가드는 UX 게이트다 — 권위 있는 차단은 서버다.
 */
export type HrGrant = 'granted' | 'denied' | 'unknown';

/** 아무도 안 걸리는 검색어 — 목록 스키마의 `search`(최대 60자). */
export const HR_GRANT_PROBE_SEARCH = '__hr-grant-probe__';

let cached: 'granted' | 'denied' | null = null;
let inflight: Promise<HrGrant> | null = null;
/** 세션이 바뀌면 오르는 번호 — 앞 세션에서 떠난 요청이 새 세션 캐시에 답을 쓰지 못하게. */
let generation = 0;

export function probeHrEmployeesGrant(): Promise<HrGrant> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  const gen = generation;
  const p: Promise<HrGrant> = apiClient
    .get('/hr/employees', { params: { search: HR_GRANT_PROBE_SEARCH } })
    .then((): HrGrant => {
      if (gen === generation) cached = 'granted';
      return 'granted';
    })
    .catch((err: unknown): HrGrant => {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 403) {
        if (gen === generation) cached = 'denied';
        return 'denied';
      }
      return 'unknown';
    })
    .finally(() => {
      if (inflight === p) inflight = null;
    });
  inflight = p;
  return p;
}

/**
 * `true` = 들어갈 수 있다 · `false` = 못 들어간다(403 · 미확정 · 로그인 안 함) · `null` = 아직 모른다(판단 보류).
 */
export function useHrEmployeesGrant(): boolean | null {
  const isAdmin = useIsAdminViewer();
  const userId = useAuthStore((s) => s.user?.id);
  const authStatus = useAuthStore((s) => s.authStatus);
  const [grant, setGrant] = useState<HrGrant | null>(() => cached);

  useEffect(() => {
    if (authStatus !== 'authed' || userId == null || isAdmin) return undefined;
    let alive = true;
    void probeHrEmployeesGrant().then((g) => { if (alive) setGrant(g); });
    return () => { alive = false; };
  }, [authStatus, userId, isAdmin]);

  if (authStatus === 'pending') return null;
  if (userId == null) return false;
  if (isAdmin) return true;
  if (grant === null) return null;
  return grant === 'granted';
}

/** 테스트 전용 — 모듈 캐시 비우기. */
export function _resetHrGrantForTests(): void {
  cached = null;
  inflight = null;
  generation += 1;
}

registerSessionCache(() => {
  cached = null;
  inflight = null;
  generation += 1;
});
